/**
 * LE CONSENTEMENT NEWSLETTER — **logique pure** (ROADMAP 6.3).
 *
 * ══ Pourquoi ce module existe séparément de l'écran ══════════════════════════
 *
 * Deux règles ne se négocient pas, et elles sont toutes deux ici plutôt que dans un
 * gestionnaire d'événement où un jour de fatigue les ferait sauter :
 *
 * 1. **Le consentement est SÉPARÉ de la connexion.** Attacher un e-mail pour sauver sa
 *    ville et accepter de recevoir des messages sont deux choses distinctes. Les
 *    confondre — une case pré-cochée, ou pire, un consentement déduit de l'inscription —
 *    est explicitement invalide (RGPD art. 4.11 : « acte positif clair »), et rendrait
 *    l'ensemble des envois illicites.
 * 2. **L'état courant est la ligne la plus RÉCENTE**, jamais un booléen qu'on écrase. Le
 *    journal est la preuve (art. 7.1) : se désabonner ajoute une ligne, n'en efface
 *    aucune. C'est aussi ce qui permet de démontrer qu'un envoi passé était licite.
 *
 * ══ La version du texte fait partie du consentement ══════════════════════════
 *
 * On ne consent pas « à la newsletter », on consent à un TEXTE. Le reformuler crée un
 * consentement différent : sans `wording_version`, une preuve ne pointerait plus vers ce
 * qui a réellement été accepté. Changer la formulation impose donc d'ajouter une version
 * ici **et** dans la contrainte de la table — les deux échouent si l'on n'en fait qu'une.
 */

/** La version du texte actuellement affiché. À incrémenter si le texte change. */
export const NEWSLETTER_WORDING_VERSION = 'v1';

export interface ConsentRecord {
  readonly granted: boolean;
  /** Horodatage SERVEUR. Une preuve datée par celui qu'elle engage n'en est pas une. */
  readonly created_at: string;
}

/**
 * L'état courant, déduit du journal.
 *
 * ⚠️ Ne suppose PAS que les lignes arrivent triées : un `order by` oublié côté requête
 * rendrait alors le premier consentement au lieu du dernier, donc un désabonnement
 * ignoré — le défaut le plus grave que cette fonction puisse produire. On trie ici.
 */
export function currentConsent(records: readonly ConsentRecord[]): boolean {
  let latest: ConsentRecord | undefined;
  for (const record of records) {
    const at = Date.parse(record.created_at);
    if (Number.isNaN(at)) continue;
    if (latest === undefined || at > Date.parse(latest.created_at)) latest = record;
  }
  return latest?.granted ?? false;
}

/**
 * Faut-il écrire une ligne pour ce que le joueur vient de cocher ?
 *
 * Non si l'état ne change pas : re-cocher une case déjà cochée n'est pas un nouvel acte
 * de volonté, et le journal se remplirait de doublons qui brouillent la preuve plutôt
 * que de la renforcer.
 */
export function shouldRecordConsent(current: boolean, wanted: boolean): boolean {
  return current !== wanted;
}
